import { useEffect, useState } from "react";
import { MessageSquare, Video, Circle, Mail, Shield } from "lucide-react";
import api from "@/lib/api";
import { relTime } from "@/lib/constants";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

export default function UserProfile({ userId, open, onOpenChange, onMessage, onCall, selfId }) {
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    if (!open || !userId) return;
    setProfile(null);
    api.get(`/users/${userId}`).then((r) => setProfile(r.data)).catch(() => {});
  }, [open, userId]);

  const isSelf = userId === selfId;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm bg-[#16181F] border-[#2E3242] text-gray-200" data-testid="user-profile-dialog">
        <DialogTitle className="sr-only">Member profile</DialogTitle>
        <DialogDescription className="sr-only">View a teammate and start a chat or call.</DialogDescription>
        {!profile ? (
          <div className="py-10 text-center text-gray-500 text-sm">Loading…</div>
        ) : (
          <div>
            <div className="flex flex-col items-center text-center pt-2">
              <div className="relative">
                <img src={profile.avatar} alt={profile.name}
                  className="w-20 h-20 rounded-full object-cover border-2 border-red-900/50" />
                <Circle className={`absolute bottom-1 right-1 w-4 h-4 rounded-full ${profile.online ? "text-emerald-500 fill-emerald-500" : "text-gray-600 fill-gray-600"}`} />
              </div>
              <h3 className="mt-3 font-display font-bold text-lg text-gray-100">{profile.name}</h3>
              <p className={`text-xs mt-0.5 ${profile.online ? "text-emerald-400" : "text-gray-500"}`}>
                {profile.online ? "Online now" : `Last active ${relTime(profile.last_seen)}`}
              </p>
            </div>

            <div className="mt-5 space-y-2 text-sm">
              <div className="flex items-center gap-2.5 text-gray-400">
                <Mail className="w-4 h-4 text-gray-600" /> <span className="truncate">{profile.email}</span>
              </div>
              <div className="flex items-center gap-2.5 text-gray-400">
                <Shield className="w-4 h-4 text-gray-600" /> <span className="capitalize">{profile.role}</span>
              </div>
            </div>

            {!isSelf && (
              <div className="mt-6 grid grid-cols-2 gap-2">
                <Button
                  data-testid="profile-message-button"
                  onClick={() => { onOpenChange(false); onMessage?.(profile); }}
                  className="bg-[#262936] hover:bg-[#30333f] text-gray-100 border border-[#2E3242] h-10"
                >
                  <MessageSquare className="w-4 h-4 mr-1.5" /> Message
                </Button>
                <Button
                  data-testid="profile-call-button"
                  onClick={() => { onOpenChange(false); onCall?.(profile); }}
                  className="ruby-gradient text-white border border-red-900/40 h-10"
                >
                  <Video className="w-4 h-4 mr-1.5" /> Video call
                </Button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
